/* ==========================================================================
   BC Cart Drawer — keep the footer in step with the line items.

   THE PROBLEM. theme.js's CartForm.cartMarkup() replaces [data-products] and
   [data-discounts] and sets [data-subtotal], and nothing else. The drawer's
   "Subtotal (N items)" label and its struck compare-at total are rendered in
   Liquid, so on a page where the cart later changes they keep the values the
   page was built with — the count reads "(0 items)" after the first add, and
   the struck total never moves.

   THE FIX, WITHOUT TOUCHING theme.js. templates/cart.ajax.liquid stamps
   data-count, data-cart-subtotal and data-cart-compare-total onto the
   .cart__items element it returns, and cartMarkup() appends that element
   wholesale. So a childList observer on [data-products] sees every rebuild
   and can read the authoritative numbers straight off it.

   theme.js is deliberately left alone: it is the file Isaac's open branches
   also touch, and a clean git merge of two edits to the same file is exactly
   how this project has lost code before.

   Deleted or folded into the drawer's own code at stage 10 only if the
   drawer stops using theme.CartForm; until then it is load-bearing.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '.bc-drawer';
  var PRODUCTS = '[data-products]';
  var ITEMS = '.cart__items';

  function root() { return document.querySelector(ROOT); }

  /* Pluralisation comes from the locale file via theme.strings, so the drawer
     says "1 item" and "2 items" in whatever language is serving. Falls back to
     the English shape only if the string is missing, which would mean the
     locale key was never added. */
  function countLabel(count) {
    var s = window.theme && theme.strings;
    var tpl = null;
    if (s) {
      if (count === 1 && s.cartSubtotalCountOne) tpl = s.cartSubtotalCountOne;
      else if (s.cartSubtotalCountOther) tpl = s.cartSubtotalCountOther;
    }
    if (!tpl) tpl = count === 1 ? 'Subtotal ({{ count }} item)' : 'Subtotal ({{ count }} items)';
    return tpl.replace(/\{\{\s*count\s*\}\}/g, count);
  }

  function sync() {
    var el = root();
    if (!el) return;

    var items = el.querySelector(PRODUCTS + ' ' + ITEMS);
    if (!items) return;

    var count = parseInt(items.getAttribute('data-count'), 10);
    var subtotal = parseInt(items.getAttribute('data-cart-subtotal'), 10);
    var compare = parseInt(items.getAttribute('data-cart-compare-total'), 10);

    /* Parse before testing, not after. "undefined" is a truthy string, so a
       `||` fallback on the raw attribute could never fire — the same trap that
       produced the £NaN in Your Setup (#334). */
    if (isFinite(count)) {
      var label = el.querySelector('.bc-drawer__subtotal-label');
      if (label) label.textContent = countLabel(count);
      el.classList.toggle('is-empty', count === 0);
    }

    /* ⚠ THE FOOTER AMOUNTS ARE LIQUID'S, NEVER FORMATTED HERE. They used to
       go through theme.Currency.formatMoney with the PAGE's money format, and
       theme.js still writes the subtotal that way. When the basket's currency
       differs from the page's — a GB basket just switched to /fr-fr/ — that
       printed a GBP total with a euro sign, "17.277 €" under £ lines (Stage
       7, 28 September). The ajax template stamps both amounts through the
       money filter, the same filter the lines use, so the footer always
       agrees with the rows. Overwrites theme.js's subtotal: this observer
       runs after cartMarkup() has finished. */
    var subtotalText = items.getAttribute('data-cart-subtotal-money');
    var compareText = items.getAttribute('data-cart-compare-money');
    var nowEl = el.querySelector('.bc-drawer__subtotal-now[data-subtotal]');
    if (nowEl && subtotalText) nowEl.innerHTML = subtotalText;

    var wasEl = el.querySelector('[data-bc-compare-total]');
    var values = el.querySelector('.bc-drawer__subtotal-values');

    /* Only show the struck figure when it is genuinely higher, so a basket at
       full price shows one number rather than the same number struck through
       beside itself. The element is created and removed rather than hidden,
       because Liquid only emits it when it applies and the two paths must
       agree. */
    if (isFinite(compare) && isFinite(subtotal) && compare > subtotal && compareText) {
      if (!wasEl && values) {
        wasEl = document.createElement('s');
        wasEl.className = 'bc-drawer__subtotal-was';
        wasEl.setAttribute('data-bc-compare-total', '');
        values.appendChild(wasEl);
      }
      if (wasEl) wasEl.innerHTML = compareText;
    } else if (wasEl) {
      wasEl.parentNode.removeChild(wasEl);
    }

    /* ⚠ THE SHARED REFRESH TRIGGER. One event, every consumer.

       theme.js's buildCart() is the single fetch per cart change: it requests
       /cart?view=ajax once and replaces [data-products]. The observer above
       fires on exactly that swap, which makes it the one reliable "the cart
       changed and the DOM now reflects it" moment available without editing
       theme.js — and it covers every route in, including adds from the buy row
       and from collection quick-add, which both go through ajaxProduct:added.

       STAGE 5 LISTENS TO THIS. The cart drawer FBT currently re-renders on
       rebuy:cart.enriched; when Rebuy goes, this replaces it, and the FBT
       keeps its own ?section_id= fetch. So: one trigger, two consumers, and
       still one cart fetch — the FBT's is for different markup, not a second
       request for the same data.

       Deliberately dispatched AFTER the footer is in step, so a listener
       reading the drawer sees consistent numbers. */
    document.dispatchEvent(new CustomEvent('bc:cart:updated', {
      detail: { count: count, subtotal: subtotal, compareTotal: compare }
    }));
  }

  /* ==========================================================================
     WALLET BUTTONS — move, never render a second copy.

     content_for_additional_checkout_buttons may be output once per page and
     layout/theme.liquid already outputs it, parked and hidden. Shopify's
     portable-wallets script injects the real button elements into that copy at
     runtime; they are not in the server HTML. Rendering our own second copy in
     the drawer was the first attempt and produced buttons at 123x42 with
     visibility:hidden and &nbsp; inside — what reveals them is being
     RE-PARENTED into a visible container, not merely sitting in one.

     So we do what Rebuy does, read from its own SmartCartV2 bundle:
     appendChild each button into our container (a move, not a clone), hide the
     source, set a populated flag so it never runs twice.
     ========================================================================== */

  var WALLETS = [
    'shop-pay-wallet-button',
    'shopify-apple-pay-button',
    'shopify-google-pay-button',
    'shopify-amazon-pay-button',
    'shopify-paypal-button'
  ].join(',');
  var SOURCE = '.additional-checkout-buttons';
  var TARGET = '[data-bc-wallets]';

  var walletsStarted = false;
  var walletObserver = null;
  var walletTimer = null;

  function stopWaiting() {
    if (walletObserver) { walletObserver.disconnect(); walletObserver = null; }
    if (walletTimer) { clearTimeout(walletTimer); walletTimer = null; }
  }

  /* ⚠ THIS RUNS REPEATEDLY, AND MUST. The first version stopped watching the
     moment it moved anything, and that is why Google Pay never appeared in our
     drawer while it appears in Rebuy's: Shopify injects each wallet only once
     its own availability check resolves, and Google Pay's resolves later than
     Shop Pay's and PayPal's. We had already disconnected by then.

     Rebuy does not stop either — read from its SmartCartV2 bundle, it starts a
     setInterval when the buttons are not there yet and keeps retrying. It also
     harvests far later in the page lifecycle (rebuy.js loads on first
     interaction, then the bundle, then Vue mounts), by which point more of
     those checks have completed. We open at the click itself, so we have to
     keep looking.

     Repeat calls cannot double-move: the buttons are MOVED out of the source,
     so a later query of the source returns only new arrivals. That is also
     what guarantees exactly one set on the page — a node can only be in one
     place. The <shopify-accelerated-checkout-cart> element is deliberately
     left behind, because it is Shopify's injection point for the late ones. */
  function harvestWallets() {
    var target = document.querySelector(TARGET);
    var source = document.querySelector(SOURCE);
    if (!target || !source) return;

    var buttons = source.querySelectorAll(WALLETS);

    /* Nothing new. Return without touching anything — moving the
       <shopify-accelerated-checkout-cart> shell before Shopify has filled it
       would relocate the injection point and leave nowhere for the real
       buttons to appear. Waiting is the whole point of this branch. */
    if (!buttons.length) return;

    Array.prototype.forEach.call(buttons, function (btn) {
      target.appendChild(btn);
    });

    source.style.display = 'none';
    target.hidden = false;

    /* Late arrivals go through the same measured pass as the first set, so a
       wallet that renders nothing still takes no space whenever it turns up. */
    settleWallets(target);
  }

  /* ⚠ A WALLET THAT RENDERS NOTHING MUST TAKE NO SPACE.
     Each moved button is a grid row, so a slot that never paints still
     reserves one — seen on device as a large blank gap between Apple Pay and
     Google Pay, almost certainly an unrendered PayPal.

     Measured rather than assumed, because "did this render" has no reliable
     declarative test: shop-pay-wallet-button reports no shadow root, so
     :empty and shadowRoot checks both lie. Height is the honest signal.

     Polled rather than read once, because the reveal is exactly what the move
     triggers — reading immediately would mark every button as empty. Each pass
     promotes anything that has gained height; the last pass condemns whatever
     still has none. A button promoted early is never re-tested. */
  function settleWallets(target) {
    var PASSES = [120, 400, 1200, 3000];

    function pass(i) {
      /* ⚠ NEVER CONDEMN WHILE THE CONTAINER ITSELF IS NOT RENDERED. Below 768px
         the wallets are display:none by design (#339, Checkout only), so every
         child measures zero height. Condemning them there would attach
         .bc-wallet--empty permanently, and they would then stay hidden even if
         the viewport later grew past the breakpoint — a tablet rotating to
         landscape, or a desktop window being widened. Skip the pass entirely;
         nothing is shown either way while the container is hidden. */
      if (!target.getClientRects().length) {
        if (i < PASSES.length - 1) setTimeout(function () { pass(i + 1); }, PASSES[i + 1] - PASSES[i]);
        return;
      }

      var kids = target.children;
      for (var k = 0; k < kids.length; k++) {
        var el = kids[k];
        if (el.classList.contains('bc-wallet--ready')) continue;
        if (el.getBoundingClientRect().height > 0) {
          el.classList.add('bc-wallet--ready');
          el.classList.remove('bc-wallet--empty');
        } else if (i === PASSES.length - 1) {
          el.classList.add('bc-wallet--empty');
        }
      }
      if (i < PASSES.length - 1) setTimeout(function () { pass(i + 1); }, PASSES[i + 1] - PASSES[i]);
    }

    setTimeout(function () { pass(0); }, PASSES[0]);
  }

  function waitForWallets() {
    var source = document.querySelector(SOURCE);
    if (!source) return;

    /* Every open, not just the first: a free catch-up on anything that has
       appeared since, which costs one querySelectorAll and needs no timers. */
    harvestWallets();

    /* The observer window opens once and is bounded. Reopening the drawer does
       not restart it — the catch-up above covers that case without leaving an
       observer alive for the life of the page. */
    if (walletsStarted) return;
    walletsStarted = true;

    /* Two independent triggers, because neither alone is reliable: a button may
       be injected before its custom element upgrades, or after. */
    walletObserver = new MutationObserver(harvestWallets);
    walletObserver.observe(source, { childList: true, subtree: true });

    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('shop-pay-wallet-button').then(harvestWallets).catch(function () {});
    }

    /* Bounded so a store with no wallets configured does not leave an observer
       running for the life of the page. Nothing is shown if it expires — the
       target stays hidden and the drawer keeps Checkout and View Cart. */
    walletTimer = setTimeout(stopWaiting, 15000);
  }

  function start() {
    /* ⚠ THE GATE THAT KEEPS THIS OFF LIVE. This file is loaded from
       header-cart-drawer.liquid, which renders on every theme whatever the flag
       says. Without this check the harvest would run on the live storefront and
       move the wallet buttons out from under Rebuy, which harvests the very
       same parked copy. The body class is emitted only when the flag is on. */
    if (!document.body || !document.body.classList.contains('bc-native-drawer')) return;

    var el = root();
    if (!el) return;
    var products = el.querySelector(PRODUCTS);
    if (!products) return;

    sync();

    /* childList on the products container only. cartMarkup() empties it and
       appends a fresh .cart__items, so every rebuild is one mutation record
       here — no need to watch the whole drawer, and no risk of reacting to
       our own footer edits. */
    new MutationObserver(sync).observe(products, { childList: true });

    /* Harvest on open, not on load: the move only reveals the buttons if the
       destination is visible, and #HeaderCart is display:none until then.
       theme.js dispatches CartDrawer:open from HeaderCart.open(). The populated
       flag makes every later open a no-op, so close-and-reopen costs nothing
       and the buttons simply stay where they were put. */
    document.addEventListener('CartDrawer:open', waitForWallets);

    /* ======================================================================
       STAGE 6 — refused ADDS.

       BOUND INSIDE start(), AFTER THE BODY-CLASS GATE, so none of this exists
       on live while Rebuy owns the drawer.

       theme.AjaxProduct already does the right thing for the buy row: it reads
       Shopify's `description`, appends it to the product form, and dispatches
       ajaxProduct:error carrying the same text. That event is the feed here —
       the message is not re-derived, it is the one theme.js already wrote.

       WHAT THIS CAN AND CANNOT DO, because it would be easy to expect more of
       it than it delivers: a REFUSED add never opens the drawer, since
       theme.js only opens on ajaxProduct:added. So this surface is read when
       the drawer is ALREADY open — which is the drawer FBT's own add, the one
       case that had nowhere to report at all. For a refused add on the PDP the
       shopper is looking at the button, and the message that matters is the
       one beside it. Both are written; neither is a substitute for the other.
       ====================================================================== */
    document.addEventListener('ajaxProduct:error', function (evt) {
      var message = evt && evt.detail && evt.detail.errorMessage;
      refusedAdd(message || genericError(), function () {
        /* theme.js appends its copy to the form as div.errors and the event
           does not say which form, so find it by the text theme.js wrote
           from this same message. */
        var written = document.querySelectorAll('form .errors');
        for (var i = 0; i < written.length; i++) {
          if (written[i].textContent === message) written[i].parentNode.removeChild(written[i]);
        }
      });
    });

    /* Any successful change clears it. Both events are dispatched after the
       cart has actually moved, so the message can never outlive the state that
       produced it — except a partial add's, which is written by this very
       rebuild and so is shown here instead of cleared. */
    document.addEventListener('bc:cart:updated', function () {
      if (pendingMessage) {
        showError(pendingMessage);
        pendingMessage = null;
      } else {
        clearError();
      }
    });
    document.addEventListener('ajaxProduct:added', clearError);

    /* A message lasts one opening. Stage 7 found the last one still showing
       when the drawer was next opened, long after it stopped being true. A
       partial add's message survives the opening it causes, because it is
       written after the rebuild, which finishes after CartDrawer:open. */
    document.addEventListener('CartDrawer:open', clearError);
  }

  /* ==========================================================================
     STAGE 7 — a PARTIAL add is not a refusal.

     Asked for more than is in stock, Shopify adds what it can and still
     answers 422 ("Only 39 items were added to your cart due to
     availability."). theme.js treats every 422 as a refusal, so the cart
     moved while the drawer, its totals and the header count kept the old
     basket until the next page load (Stage 7, 28 September).

     So the real cart decides, never the wording: Shopify localises it, and
     English copy is not a machine key here. If the count moved, rebuild
     through theme.js (which also updates the header bubble), open the
     drawer, and show Shopify's message once, in it — the caller's
     clearButton removes the copy beside the button, so the shopper sees it
     in one place, not two. If it did not, this is a true refusal and
     today's behaviour stands: the message beside the button, and in the
     drawer only if it is already open.
     ========================================================================== */

  var pendingMessage = null;

  function cartRoute(path) {
    return ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + path;
  }

  function shownCount() {
    var items = document.querySelector(ROOT + ' ' + PRODUCTS + ' ' + ITEMS);
    return items ? parseInt(items.getAttribute('data-count'), 10) : NaN;
  }

  function refusedAdd(message, clearButton) {
    if (!message) return;
    fetch(cartRoute('cart.js'), { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var shown = shownCount();
        if (!cart || typeof cart.item_count !== 'number' || !isFinite(shown) || cart.item_count === shown) {
          showError(message);
          return;
        }
        pendingMessage = message;
        if (typeof clearButton === 'function') clearButton();
        document.dispatchEvent(new CustomEvent('cart:build'));
        var panel = document.getElementById('HeaderCart');
        if (!panel || !panel.classList.contains('is-active')) {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      })
      .catch(function () { showError(message); });
  }

  /* ---- the drawer's own message -------------------------------------- */

  function errorEl() { return document.querySelector('[data-bc-drawer-error]'); }

  function genericError() {
    return (window.theme && theme.strings && theme.strings.cartUpdateError) ||
      'Sorry, we could not update your basket. Please try again.';
  }

  function showError(message) {
    var el = errorEl();
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
  }

  function clearError() {
    var el = errorEl();
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  /* For assets/bc-cart-fbt.js, whose own add is the main thing this reports,
     and for assets/pdp-test.js. Exposed rather than duplicated so there is one
     message surface with one set of rules about when it clears. refusedAdd is
     for any ADD that failed, so a partial one is caught wherever it began. */
  window.bcCartDrawer = {
    showError: showError,
    clearError: clearError,
    refusedAdd: refusedAdd
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
